import React, { useState } from "react";
import styled from "styled-components";
import ConfirmationModal from "../Common/ConfirmationModal";
import API from "../../util/api";
import UpdateImplementationModal from "./UpdateImplementationModal";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const parseStatus = (status) => {
  if (status === undefined || status === null) return null;
  if (typeof status === 'number' && !Number.isNaN(status)) return status;
  const parsed = Number.parseInt(String(status), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const isCertifiedStatus = (status) => parseStatus(status) === 1;

const hasDownloadFlag = (grade) => {
  if (!grade) return false;
  if (grade.download_date) return true;
  const downloaded = grade.downloaded;
  return (
    downloaded === "1" ||
    downloaded === 1 ||
    downloaded === true
  );
};

const CompletionContainer = styled.div`
  background-color: #ffffff;
  border-radius: 5px;
  overflow: hidden;
  @media (max-width: ${breakpoints.tablet}px) {
    margin: 0 auto;
    width: 95%;
    max-width: 600px;
  }
  /* phone: even narrower */
  @media (max-width: ${breakpoints.phone}px) {
    margin: 0 auto;
    width: 100%;
  }
`;

const CompletionHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding: 10px;
  background-color: ${(p) => p.colors.buttonBackground};
  color: ${(p) => p.colors.buttonText};

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 14px;
    padding: 8px;
    text-align: center;
  }
`;

const ContentWrapper = styled.div`
  margin: 0 auto;
  width: 95%;
  padding: 20px 0;

  @media (max-width: ${breakpoints.tablet}px) {
    width: 90%;
    padding: 16px 0;
  }
  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    max-width: 350px;
    padding: 12px 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  margin: 0 auto;
  border-collapse: collapse;

  @media (max-width: ${breakpoints.phone}px) {
    display: block;
  }
`;

const TableHeader = styled.th`
  background-color: #ffffff;
  color: ${(p) => p.colors.buttonBackground};
  font-size: 13px;
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ccc;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 12px;
    padding: 6px 8px;
  }
`;

const TableRow = styled.tr`
  background-color: ${(p) => (p.iseven ? "#dcdcdc" : "#ffffff")};

  @media (max-width: ${breakpoints.phone}px) {
    display: table-row;
  }
`;

const TableCell = styled.td`
  padding: 10px;
  font-size: 11px;
  vertical-align: top;

  @media (max-width: ${breakpoints.phone}px) {
    padding: 6px 8px;
    font-size: 11px;
    min-width: 80px;
  }
`;

const ActionLink = styled.button.attrs({ type: "button" })`
  font-size: 11px;
  color: ${(p) => p.colors.buttonBackground};
  cursor: pointer;
  margin-right: 8px;
  background: none;
  border: none;
  padding: 0;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #555;
    cursor: default;
    text-decoration: none;
  }

  @media (max-width: ${breakpoints.phone}px) {
    display: inline-block;
    font-size: 11px;
    margin-bottom: 4px;
  }
`;

const CertifyAction = styled(ActionLink)`
  text-decoration: underline;

  &:hover {
    text-decoration: underline;
  }
`;

const DownloadPrompt = styled.span`
  font-size: 11px;
  color: ${(p) => p.colors.buttonBackground};
  margin-right: 8px;
`;

const CertificationNotice = styled.div`
  margin: 0 0 14px 0;
  font-size: 11px;
  color: #000000;
  font-family: 'Source Sans Pro', sans-serif !important;
  line-height: 1.45;
`;

export default function ImplementationsRequiringCompletion({
  colors,
  implementations = [],
  isLoading = false,
  onRefresh,
}) {
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [resultOpen, setResultOpen]         = useState(false);
  const [resultMsg, setResultMsg]           = useState("");
  const [resultStatus, setResultStatus]     = useState("Success");
  const [pendingImplementation, setPending] = useState(null);
  const [updateImplementation, setUpdateImplementation] = useState(null);

  const resultFailed = resultStatus !== "Success";

  if (isLoading) {
    return (
      <CompletionContainer>
        <CompletionHeader colors={colors}>
          Implementations Requiring Completion
        </CompletionHeader>
        <ContentWrapper>
          <Table>
            <tbody>
              <TableRow iseven={false}>
                <TableCell colSpan={5} style={{ textAlign: "center" }}>
                  Loading…
                </TableCell>
              </TableRow>
            </tbody>
          </Table>
        </ContentWrapper>
      </CompletionContainer>
    );
  }

  // no implementation case
  if (!implementations || implementations.length === 0) {
    return (
      <CompletionContainer>
        <CompletionHeader colors={colors}>
          Implementations Requiring Completion
        </CompletionHeader>
        <ContentWrapper>
          <Table>
            <tbody>
              <TableRow iseven={false}>
                <TableCell colSpan={5} style={{ textAlign: "center" }}>
                  You do not have any implementations to certify at this time.
                </TableCell>
              </TableRow>
            </tbody>
          </Table>
        </ContentWrapper>
      </CompletionContainer>
    );
  }

  // single implementation row
  const handleCertifyClick = (imp) => {
    setPending(imp);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (!pendingImplementation?.implementation_id) {
      setPending(null);
      return;
    }
    try {
      const resp = await API.certifySubscriptionTeacher({
        implementation_id: pendingImplementation.implementation_id,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setResultMsg(resp.message || (status === "Failed" ? "Certification failed." : "Certification complete."));
      setResultStatus(status);
    } catch (err) {
      setResultMsg(err.message || "Certification failed.");
      setResultStatus("Failed");
    } finally {
      setResultOpen(true);
      setPending(null);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setResultStatus("Success");
    if (!resultFailed && typeof onRefresh === "function") {
      onRefresh();
    }
  };

  const handleUpdateClick = (imp) => {
    setUpdateImplementation(imp);
  };

  const handleCloseUpdate = () => setUpdateImplementation(null);

  return (
    <CompletionContainer>
      <CompletionHeader colors={colors}>
        Implementations Requiring Completion
      </CompletionHeader>

      <ContentWrapper>
        <CertificationNotice>
          <strong>Implementations Requiring Completion Certification</strong>
          <br />
          Your Subscription Admin has required that you certify you have completed your implementations. Click "Certify Complete" to state you have completed the implementation.
        </CertificationNotice>

        <Table>
          <thead>
            <tr>
              <TableHeader colors={colors}>Subscription</TableHeader>
              <TableHeader colors={colors}>School</TableHeader>
              <TableHeader colors={colors}>Date</TableHeader>
              <TableHeader colors={colors}>Grades</TableHeader>
              <TableHeader colors={colors}>Status</TableHeader>
            </tr>
          </thead>
          <tbody>
            {implementations.map((imp, idx) => {
              const gradeCodes = Array.isArray(imp.grade_codes)
                ? imp.grade_codes
                : [];
              const requiresCertification = gradeCodes.some(
                (g) => !isCertifiedStatus(g.status ?? g.status1)
              );
              const hasDownloadedGrades = gradeCodes.some((g) =>
                hasDownloadFlag(g)
              );

              return (
                <TableRow
                  key={imp.implementation_id || idx}
                  iseven={idx % 2 === 0}
                >
                  <TableCell>{imp.invoice_number}</TableCell>
                  <TableCell>{imp.org_name}</TableCell>
                  <TableCell>
                    [{imp.start_date} to {imp.end_date}]
                  </TableCell>
                  <TableCell>
                    {gradeCodes.map((g, i) => {
                      const certified = isCertifiedStatus(g.status ?? g.status1);
                      const hasDownloadInfo = hasDownloadFlag(g);
                      const downloaded = !certified && hasDownloadInfo;
                      const style = {
                        textDecoration: downloaded ? "underline" : "none",
                        fontWeight: certified ? "bold" : "normal",
                      };
                      return (
                        <span
                          key={
                            g.ip_grade_id ||
                            `${imp.implementation_id}-${g.grade_code}`
                          }
                          style={style}
                        >
                          {g.grade_code}
                          {i < gradeCodes.length - 1 && ",\u00A0"}
                        </span>
                      );
                    })}
                  </TableCell>
                  <TableCell>
                    {hasDownloadedGrades ? (
                      <CertifyAction
                        colors={colors}
                        disabled={!requiresCertification}
                        onClick={() => handleCertifyClick(imp)}
                      >
                        Certify Complete
                      </CertifyAction>
                    ) : (
                      <DownloadPrompt colors={colors}>
                        Download Curriculum
                      </DownloadPrompt>
                    )}
                    <ActionLink
                      colors={colors}
                      onClick={() => handleUpdateClick(imp)}
                    >
                      Update
                    </ActionLink>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </ContentWrapper>

      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Completion"
        message="Are you sure you have completed this implementation?"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      <ConfirmationModal
        show={resultOpen}
        title={resultFailed ? "Error" : "Success"}
        message={resultMsg}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />

      {updateImplementation && (
        <UpdateImplementationModal
          show
          colors={colors}
          implementation={updateImplementation}
          onCancel={handleCloseUpdate}
          onSuccess={() => {
            handleCloseUpdate();
            onRefresh && onRefresh();
          }}
        />
      )}
    </CompletionContainer>
  );
}
